/**
 * W1-11b (app-shell-ui): the banners row under the top bar (DESIGN.md §3.2, §4.4, §5.13): the status items modules
 * register for the 'banner' slot (e.g. "Preview differs from export", W2-02; each decides when it shows) and the
 * shell's own notices:
 *  - storage health: writes failing (red, announced), the storage worker reconnecting, storage almost full (less
 *    than 200 MB free) and storage the browser may clear (persistence denied, with "Protect my charts"); the
 *    failing, full and unprotected notices offer "Download backup now";
 *  - the export reminder: the open chart has changes and was not exported for 7 days (or never, 7 days after it was
 *    started), with "Export...".
 * The quota, "may clear" and export notices can be dismissed: the UI service keeps them away (across projects and
 * remounts) until the user has edited for another 2 hours, then they come back as a reminder. Banners count the
 * editing time (`ui.noteEditing` on every committed change). Notices pair an icon with words.
 *
 * One row (W3-01, UX-002): every notice gets a slot, but only one is shown at a time, the most serious first
 * (failing writes and reconnecting, then the modules' notices in their order, then the storage warnings, then the
 * export reminder). With more than one, the row shows "1 of 3" and a button to the next; every notice but the
 * failing-storage ones has "Later" (dismissed like the shell's own notices, so module notices come back after two
 * hours of editing too). Which slots have something to show is read from the DOM (a module's component decides by
 * rendering nothing), so a module notice never has to tell the shell. Slots of hidden or dismissed notices stay
 * mounted (a status item can host something that is not drawn in the row, like the preview's full-window host).
 * While a modal dialog is open the row is hidden (it keeps its height, so nothing behind the dialog moves). The row
 * is used by the editor (under the top bar) and above the other pages (Library, Settings) by the router.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
  type RefObject,
} from 'react';
import type { ProjectId } from '../../core/model/types';
import type { StorageHealth } from '../../persist/types';
import type { DocStore } from '../../state/types';
import { Button } from '../../ui/Button';
import { Icon } from '../../ui/Icon';
import { IconButton } from '../../ui/IconButton';
import type { AppServices, Registry } from '../registry/types';
import type { ShellUiService, UiSnapshot } from '../uiService';
import { ErrorBoundary } from './ErrorBoundary';
import { useStore, useText, useUi, type ShellText } from './ShellContext';
import { downloadBackup, formatBytes, lowSpace, protectCharts, timeAgo, useStorageHealth } from './StatusChips';
import { m } from './shell.messages';
import s from './Shell.module.css';

/** A chart changed and not exported for this long gets a reminder (DESIGN.md §5.13). */
export const EXPORT_REMINDER_MS = 7 * 24 * 60 * 60 * 1000;
/** How often the banners re-check time-based notices without edits. */
const CLOCK_REFRESH_MS = 10 * 60 * 1000;
/** Edits refresh the banners' clock at most this often. */
const CLOCK_EDIT_STEP_MS = 60_000;

export type StorageNotice = 'writeError' | 'reconnecting' | 'quota' | 'notPersisted';

/** Banner id of a storage notice (`ui.dismissBanner`). */
export function storageBannerId(notice: StorageNotice): string {
  return `storage.${notice}`;
}

/** Banner id of a project's export reminder (`ui.dismissBanner`). */
export function exportBannerId(project: ProjectId): string {
  return `export.${project}`;
}

/** Banner id of a module's status item in the banner row (`ui.dismissBanner`). */
export function itemBannerId(item: string): string {
  return `item.${item}`;
}

const STORAGE_NOTICES: readonly StorageNotice[] = ['writeError', 'reconnecting', 'quota', 'notPersisted'];
const selectDismissed = (snap: UiSnapshot): readonly string[] => snap.dismissedBanners;
const selectDialogs = (snap: UiSnapshot): UiSnapshot['dialogs'] => snap.dialogs;

/** A modal dialog is open (registered modal dialogs and the system confirm / choose dialogs). */
export function modalDialogOpen(dialogs: UiSnapshot['dialogs'], registry: Pick<Registry, 'dialog'>): boolean {
  return dialogs.some((d) => d.system !== null || (d.id !== null && registry.dialog(d.id)?.kind === 'modal'));
}

/** The storage notice to show for a health report (most serious first), or null. */
export function storageNotice(h: StorageHealth | null, dismissed: ReadonlySet<StorageNotice>): StorageNotice | null {
  if (h === null) return null;
  if (h.lastWriteError !== null) return 'writeError';
  if (!h.workerAlive) return 'reconnecting';
  if (lowSpace(h) && !dismissed.has('quota')) return 'quota';
  if (!h.persisted && !dismissed.has('notPersisted')) return 'notPersisted';
  return null;
}

export interface ExportReminder {
  /** Epoch ms the reminder counts from: the last export, else when the chart was started. */
  readonly since: number;
  /** The chart was never exported. */
  readonly never: boolean;
}

/**
 * The "changed but not exported for 7 days" reminder of a project, or null: it has changes after its last export
 * (any change when it was never exported) and the last export (else its creation, `createdAt`, null while unknown)
 * is at least EXPORT_REMINDER_MS old.
 */
export function exportReminder(
  store: Pick<DocStore, 'doc' | 'seq'>,
  createdAt: number | null,
  now: number,
): ExportReminder | null {
  const last = store.doc.editor.lastExport;
  const changed = last === null ? store.seq > 0 : store.seq > last.seq;
  if (!changed) return null;
  const since = last?.at ?? createdAt;
  if (since === null || now - since < EXPORT_REMINDER_MS) return null;
  return { since, never: last === null };
}

/**
 * Counts editing time (every committed change of the open chart) and returns a clock for time-based notices:
 * refreshed by edits (at most once a minute) and every CLOCK_REFRESH_MS.
 */
function useEditingClock(store: DocStore | null, ui: ShellUiService): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
    }, CLOCK_REFRESH_MS);
    const off =
      store?.subscribeCommits(() => {
        const t = Date.now();
        ui.noteEditing(t);
        setNow((prev) => (t - prev >= CLOCK_EDIT_STEP_MS ? t : prev));
      }) ?? null;
    return () => {
      clearInterval(timer);
      off?.();
    };
  }, [store, ui]);
  return now;
}

const noop = (): void => undefined;

/** Export state of a chart: last export and whether it changed since (the reminder depends on nothing else). */
function exportStateKey(store: DocStore | null): string {
  if (store === null) return '';
  const last = store.doc.editor.lastExport;
  const changed = last === null ? store.seq > 0 : store.seq > last.seq;
  return `${String(last?.at ?? -1)}:${changed ? 'changed' : 'same'}`;
}

/** Re-renders when the open chart is exported or changes for the first time after an export. */
function useExportState(store: DocStore | null): string {
  const subscribe = useCallback(
    (cb: () => void): (() => void) =>
      store === null
        ? noop
        : store.subscribeCommits(() => {
            cb();
          }),
    [store],
  );
  const get = useCallback(() => exportStateKey(store), [store]);
  return useSyncExternalStore(subscribe, get, get);
}

/** When the open project was created (Library record), for charts never exported; null until known. */
function useProjectCreatedAt(services: AppServices, project: ProjectId | null): number | null {
  const [known, setKnown] = useState<{ readonly id: ProjectId; readonly at: number } | null>(null);
  const { persist } = services;
  useEffect(() => {
    if (project === null) return undefined;
    let alive = true;
    persist
      .listProjects()
      .then((list) => {
        const rec = list.find((p) => p.id === project);
        if (alive && rec !== undefined) setKnown({ id: project, at: rec.createdAt });
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [persist, project]);
  return known?.id === project ? known.at : null;
}

function StorageBanner(props: {
  readonly services: AppServices;
  readonly notice: StorageNotice;
  readonly health: StorageHealth;
  readonly text: ShellText;
}) {
  const { services, notice, health, text } = props;
  const severe = notice === 'writeError';
  const message =
    notice === 'writeError'
      ? text.t(m.bannerWriteError, { error: health.lastWriteError ?? text.t(m.unknownError) })
      : notice === 'reconnecting'
        ? text.t(m.bannerReconnecting)
        : notice === 'quota'
          ? text.t(m.bannerQuota, {
              free: formatBytes(Math.max(0, health.quotaBytes - health.usageBytes), text),
              used: formatBytes(health.usageBytes, text),
              quota: formatBytes(health.quotaBytes, text),
            })
          : text.t(m.bannerNotPersisted);
  return (
    <div
      className={s.banner}
      data-kind={severe ? 'error' : notice === 'reconnecting' ? 'info' : 'warning'}
      data-banner={storageBannerId(notice)}
      role={severe ? 'alert' : 'status'}
    >
      <Icon
        name={
          severe
            ? 'CloudAlert'
            : notice === 'reconnecting'
              ? 'CloudOff'
              : notice === 'quota'
                ? 'HardDrive'
                : 'ShieldAlert'
        }
        size={16}
      />
      <span className={s.bannerText}>{message}</span>
      {notice !== 'reconnecting' && services.store !== null && (
        <Button
          size="sm"
          variant={severe ? 'primary' : 'secondary'}
          icon="HardDriveDownload"
          onClick={() => {
            downloadBackup(services);
          }}
        >
          {text.t(m.downloadBackup)}
        </Button>
      )}
      {notice === 'notPersisted' && (
        <Button
          size="sm"
          variant="secondary"
          icon="ShieldCheck"
          onClick={() => {
            void protectCharts(services);
          }}
        >
          {text.t(m.protectCharts)}
        </Button>
      )}
    </div>
  );
}

function ExportBanner(props: {
  readonly services: AppServices;
  readonly reminder: ExportReminder;
  readonly now: number;
  readonly text: ShellText;
}) {
  const { services, reminder, now, text } = props;
  const when = timeAgo(reminder.since, now, text);
  const canExport = services.registry.command('file.export') !== undefined;
  return (
    <div className={s.banner} data-kind="info" data-banner="export" role="status">
      <Icon name="FileClock" size={16} />
      <span className={s.bannerText}>
        {text.t(reminder.never ? m.bannerExportNever : m.bannerExportStale, { when })}
      </span>
      {canExport && (
        <Button
          size="sm"
          variant="secondary"
          icon="PackageCheck"
          onClick={() => {
            void services.registry.runAny('file.export', { quick: false }, 'button');
          }}
        >
          {text.t(m.exportNow)}
        </Button>
      )}
    </div>
  );
}

interface NoticeEntry {
  readonly id: string;
  readonly node: ReactNode;
  /** Banner id dismissed by "Later" (null: the notice cannot be put off). */
  readonly later: string | null;
  /** The shell's own notices are known to show something; a module's is read from the DOM. */
  readonly known: boolean;
}

/** Ids of the slots under `row` that render something (a module's notice renders nothing when it has nothing to say). */
function filledSlots(row: HTMLElement): string[] {
  const out: string[] = [];
  for (const el of row.querySelectorAll<HTMLElement>(':scope > [data-notice]')) {
    if (el.childNodes.length > 0) out.push(el.dataset.notice ?? '');
  }
  return out;
}

/** Follows which notice slots have content (DOM mutations of the row). */
function useFilledSlots(row: RefObject<HTMLElement | null>): readonly string[] {
  const [filled, setFilled] = useState<readonly string[]>([]);
  useLayoutEffect(() => {
    const el = row.current;
    if (el === null) return undefined;
    const read = (): void => {
      const next = filledSlots(el);
      setFilled((prev) => (prev.length === next.length && prev.every((x, i) => x === next[i]) ? prev : next));
    };
    read();
    if (typeof MutationObserver === 'undefined') return undefined;
    const mo = new MutationObserver(read);
    mo.observe(el, { childList: true, subtree: true });
    return () => {
      mo.disconnect();
    };
  }, [row]);
  return filled;
}

export function Banners(props: { readonly services: AppServices; readonly ui: ShellUiService }) {
  const { services, ui } = props;
  const text = useText(services);
  const store = useStore(services);
  const health = useStorageHealth(services);
  const now = useEditingClock(store, ui);
  useExportState(store);
  const project = store?.projectId ?? null;
  const createdAt = useProjectCreatedAt(services, project);
  const dismissedIds = useUi(ui, selectDismissed);
  const dialogs = useUi(ui, selectDialogs);
  const modal = modalDialogOpen(dialogs, services.registry);
  const dismissed = useMemo(
    () => new Set(STORAGE_NOTICES.filter((n) => dismissedIds.includes(storageBannerId(n)))),
    [dismissedIds],
  );
  const notice = storageNotice(health, dismissed);
  const reminder =
    store === null || dismissedIds.includes(exportBannerId(store.projectId))
      ? null
      : exportReminder(store, createdAt, now);
  const items = services.registry.statusItems('banner');
  const severe = notice === 'writeError' || notice === 'reconnecting';

  const entries: NoticeEntry[] = [];
  const storageEntry: NoticeEntry | null =
    notice !== null && health !== null
      ? {
          id: storageBannerId(notice),
          node: <StorageBanner services={services} notice={notice} health={health} text={text} />,
          later: severe ? null : storageBannerId(notice),
          known: true,
        }
      : null;
  if (storageEntry !== null && severe) entries.push(storageEntry);
  for (const item of items) {
    entries.push({
      id: itemBannerId(item.id),
      node: (
        <ErrorBoundary variant="silent" services={services} label={item.id}>
          <item.component services={services} />
        </ErrorBoundary>
      ),
      later: itemBannerId(item.id),
      known: false,
    });
  }
  if (storageEntry !== null && !severe) entries.push(storageEntry);
  if (reminder !== null && store !== null) {
    entries.push({
      id: exportBannerId(store.projectId),
      node: <ExportBanner services={services} reminder={reminder} now={now} text={text} />,
      later: exportBannerId(store.projectId),
      known: true,
    });
  }

  const rowRef = useRef<HTMLDivElement>(null);
  const filled = useFilledSlots(rowRef);
  const shown = entries.filter((e) => (e.known || filled.includes(e.id)) && !dismissedIds.includes(e.id));
  const [cursor, setCursor] = useState<string | null>(null);
  const currentIndex = Math.max(
    0,
    shown.findIndex((e) => e.id === cursor),
  );
  const current = shown[currentIndex] ?? null;
  const count = shown.length;

  return (
    <div
      ref={rowRef}
      className={s.banners}
      aria-label={text.t(m.bannersLabel)}
      role="region"
      data-chrome=""
      data-notices={count}
      data-suppressed={modal ? 'true' : undefined}
    >
      {entries.map((e) => (
        <div key={e.id} className={s.noticeSlot} data-notice={e.id} hidden={e.id !== current?.id}>
          {e.node}
        </div>
      ))}
      {current !== null && (count > 1 || current.later !== null) && (
        <div className={s.noticeControls}>
          {count > 1 && (
            <>
              <span className={s.noticeCount} aria-hidden="true">
                {text.t(m.noticeCount, { index: currentIndex + 1, count })}
              </span>
              <IconButton
                icon="ChevronRight"
                size="sm"
                label={{ key: m.noticeNext, params: { index: currentIndex + 1, count } }}
                onClick={() => {
                  setCursor(shown[(currentIndex + 1) % count]?.id ?? null);
                }}
              />
            </>
          )}
          {current.later !== null && (
            <Button
              size="sm"
              variant="ghost"
              title={text.t(m.noticeLaterDetail)}
              onClick={() => {
                const id = current.later;
                if (id !== null) ui.dismissBanner(id);
              }}
            >
              {text.t(m.noticeLater)}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
